import React from 'react'

export interface ButtonProps {
  children?: React.ReactNode
  variant?: 'primary' | 'normal' | 'link' | 'icon' | 'inline-icon' | 'inline-link'
  iconName?: string
  iconAlign?: 'left' | 'right'
  disabled?: boolean
  loading?: boolean
  /**
   * Optional explanation shown as a native `title` tooltip when the button is
   * disabled (or loading). Lets a restricted control explain why it is disabled
   * without wrapping it in an external element. Has no effect when enabled.
   */
  disabledReason?: string
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>
  type?: 'button' | 'submit' | 'reset'
  className?: string
  style?: React.CSSProperties
  ariaLabel?: string
  fontSize?: string
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'normal',
  disabled = false,
  loading = false,
  disabledReason,
  onClick,
  type = 'button',
  className,
  style,
  ariaLabel,
}) => {
  const baseStyle: React.CSSProperties = {
    padding: '8px 16px',
    borderRadius: '8px',
    border: '1px solid',
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    transition: 'all 0.2s',
    opacity: disabled || loading ? 0.6 : 1,
    ...style,
  }

  const variantStyles: Record<string, React.CSSProperties> = {
    primary: {
      backgroundColor: '#0972d3',
      color: 'white',
      borderColor: '#0972d3',
    },
    normal: {
      backgroundColor: 'white',
      color: '#000716',
      borderColor: '#d1d5db',
    },
    link: {
      backgroundColor: 'transparent',
      color: '#0972d3',
      border: 'none',
      padding: '4px 8px',
    },
    icon: {
      backgroundColor: 'transparent',
      border: 'none',
      padding: '4px',
    },
  }

  return (
    <button
      type={type}
      onClick={disabled || loading ? undefined : onClick}
      disabled={disabled || loading}
      title={(disabled || loading) && disabledReason ? disabledReason : undefined}
      className={className}
      style={{ ...baseStyle, ...variantStyles[variant] }}
      aria-label={ariaLabel}
    >
      {loading ? 'Loading...' : children}
    </button>
  )
}
